<template>
  <div class="df-editor">

    <div class="df-head">
      <div>
        <h4 class="df-title">
          <span class="material-symbols-outlined">event</span>
          Formats de date
        </h4>
        <p class="df-hint">
          Comment lire les dates de cette source. Chaque champ affiche en direct
          ce que le format produirait, pour vérifier sans deviner.
        </p>
      </div>

      <button type="button" class="df-raw-toggle" @click="rawMode = !rawMode">
        {{ rawMode ? 'Vue guidée' : 'Vue JSON' }}
      </button>
    </div>

    <div v-if="parseError" class="df-error">
      <span class="material-symbols-outlined">warning</span>
      Le contenu enregistré n'est pas un JSON valide. Corrigez-le en vue JSON
      pour reprendre l'édition guidée.
    </div>

    <div v-if="rawMode || parseError">
      <textarea
          class="df-textarea"
          rows="8"
          :disabled="!editable"
          :value="value || ''"
          @input="onRawInput($event.target.value)"
      ></textarea>
    </div>

    <template v-else>

      <!-- Format principal -->
      <div class="df-field">
        <label class="df-label">
          Format de la colonne date
          <em>obligatoire — s'applique à la valeur lue dans la colonne de date</em>
        </label>

        <div class="df-input-row">
          <input
              type="text"
              class="df-input"
              :disabled="!editable"
              v-model="model.DefaultFormat"
              placeholder="d/m/Y H:i:s"
              @input="emit"
          />
          <span class="df-preview" :class="{ invalid: !previewDefault }">
            {{ previewDefault || 'format non reconnu' }}
          </span>
        </div>

        <div class="df-presets" v-if="editable">
          <button
              v-for="p in presets"
              :key="p.fmt"
              type="button"
              class="df-preset"
              @click="applyPreset('DefaultFormat', p.fmt)"
          >{{ p.label }}</button>
        </div>
      </div>

      <!-- Format du nom de fichier -->
      <div class="df-field">
        <label class="df-label">
          Format du nom de fichier
          <em>facultatif — nécessaire uniquement pour les exceptions ci-dessous</em>
        </label>

        <div class="df-input-row">
          <input
              type="text"
              class="df-input"
              :disabled="!editable"
              v-model="model.FileFormat"
              placeholder="Ymd"
              @input="emit"
          />
          <span class="df-preview" :class="{ invalid: model.FileFormat && !previewFile }">
            {{ model.FileFormat ? (previewFile || 'format non reconnu') : '—' }}
          </span>
        </div>

        <p class="df-sub">
          S'applique au nom du fichier <strong>sans son extension</strong> :
          <code>20240115.csv</code> se lit avec <code>Ymd</code>.
        </p>
      </div>

      <!-- Exceptions -->
      <div class="df-field">
        <label class="df-label">
          Exceptions par période
          <em>facultatif — un autre format pour une plage de fichiers</em>
        </label>

        <p class="df-sub" v-if="!model.FileFormat">
          Renseignez d'abord le format du nom de fichier : c'est lui qui permet
          de situer un fichier dans une période.
        </p>

        <template v-else>
          <div v-if="!model.Overrides.length" class="df-empty">
            Aucune exception. Tous les fichiers sont lus avec le format principal.
          </div>

          <table v-else class="df-table">
            <thead>
            <tr>
              <th>Du fichier</th>
              <th>Au fichier</th>
              <th>Format de date à utiliser</th>
              <th class="df-action-col"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(ov, i) in model.Overrides" :key="i">
              <td>
                <input
                    type="text"
                    class="df-input"
                    :disabled="!editable"
                    v-model="ov.from"
                    placeholder="20240101.csv"
                    @input="emit"
                />
              </td>
              <td>
                <input
                    type="text"
                    class="df-input"
                    :disabled="!editable"
                    v-model="ov.to"
                    placeholder="20240331.csv"
                    @input="emit"
                />
              </td>
              <td>
                <input
                    type="text"
                    class="df-input"
                    :disabled="!editable"
                    v-model="ov.Format"
                    placeholder="Y-m-d H:i"
                    @input="emit"
                />
                <span class="df-preview inline" :class="{ invalid: !preview(ov.Format) }">
                  {{ preview(ov.Format) || 'format non reconnu' }}
                </span>
              </td>
              <td class="df-action-col">
                <button
                    v-if="editable"
                    type="button"
                    class="df-remove"
                    title="Retirer cette exception"
                    @click="removeOverride(i)"
                >
                  <span class="material-symbols-outlined">close</span>
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <button v-if="editable" type="button" class="df-add" @click="addOverride">
            <span class="material-symbols-outlined">add</span>
            Ajouter une exception
          </button>
        </template>
      </div>

      <!-- Aide-mémoire -->
      <details class="df-cheat">
        <summary>Codes de format</summary>
        <div class="df-cheat-grid">
          <span v-for="t in tokens" :key="t.code">
            <code>{{ t.code }}</code> {{ t.label }}
          </span>
        </div>
      </details>
    </template>
  </div>
</template>

<script>
// Rendu d'un format de date PHP, pour l'aperçu. Couvre les codes réellement
// utilisés dans les fichiers de mesure ; un code inconnu est laissé tel quel.
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatPhp(date, fmt) {
  if (!fmt) return "";

  let out = "";

  for (let i = 0; i < fmt.length; i++) {
    const c = fmt[i];

    // Échappement PHP : le caractère suivant est littéral.
    if (c === "\\") {
      out += fmt[++i] || "";
      continue;
    }

    switch (c) {
      case "d": out += pad(date.getDate()); break;
      case "j": out += date.getDate(); break;
      case "m": out += pad(date.getMonth() + 1); break;
      case "n": out += date.getMonth() + 1; break;
      case "Y": out += date.getFullYear(); break;
      case "y": out += String(date.getFullYear()).slice(-2); break;
      case "H": out += pad(date.getHours()); break;
      case "G": out += date.getHours(); break;
      case "h": out += pad(((date.getHours() + 11) % 12) + 1); break;
      case "g": out += ((date.getHours() + 11) % 12) + 1; break;
      case "i": out += pad(date.getMinutes()); break;
      case "s": out += pad(date.getSeconds()); break;
      case "v": out += "000"; break;
      case "u": out += "000000"; break;
      case "A": out += date.getHours() < 12 ? "AM" : "PM"; break;
      case "a": out += date.getHours() < 12 ? "am" : "pm"; break;
      case "M": out += MONTHS[date.getMonth()].slice(0, 3); break;
      case "F": out += MONTHS[date.getMonth()]; break;
      case "D": out += DAYS[date.getDay()].slice(0, 3); break;
      case "l": out += DAYS[date.getDay()]; break;
      case "T": out += "UTC"; break;
      default: out += c;
    }
  }

  return out;
}

// Un format n'est utile que s'il contient au moins un code de date.
const HAS_TOKEN = /[djmnYyHGhgisvuAaMFDlT]/;

export default {
  name: "DateFormatsEditor",

  props: {
    // Chaîne JSON : { DefaultFormat, FileFormat, Overrides: [{from, to, Format}] }
    value: {type: String, default: ""},
    editable: {type: Boolean, default: true},
  },

  data() {
    return {
      model: {DefaultFormat: "", FileFormat: "", Overrides: []},
      rawMode: false,
      parseError: false,
      lastEmitted: null,

      sample: new Date(2024, 0, 15, 14, 30, 45),

      presets: [
        {label: "15/01/2024 14:30:45", fmt: "d/m/Y H:i:s"},
        {label: "2024-01-15 14:30:45", fmt: "Y-m-d H:i:s"},
        {label: "2024-01-15T14:30:45", fmt: "Y-m-d\\TH:i:s"},
        {label: "15/01/2024 14:30", fmt: "d/m/Y H:i"},
        {label: "20240115", fmt: "Ymd"},
      ],

      tokens: [
        {code: "d", label: "jour 01-31"},
        {code: "j", label: "jour 1-31"},
        {code: "m", label: "mois 01-12"},
        {code: "n", label: "mois 1-12"},
        {code: "Y", label: "année 4 chiffres"},
        {code: "y", label: "année 2 chiffres"},
        {code: "H", label: "heure 00-23"},
        {code: "i", label: "minutes"},
        {code: "s", label: "secondes"},
        {code: "M", label: "mois abrégé"},
        {code: "F", label: "mois en toutes lettres"},
        {code: "\\\\", label: "échappe le caractère suivant"},
      ],
    };
  },

  computed: {
    previewDefault() {
      return this.preview(this.model.DefaultFormat);
    },

    previewFile() {
      return this.preview(this.model.FileFormat);
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v === this.lastEmitted) return;
        this.hydrate(v);
      },
    },
  },

  methods: {
    preview(fmt) {
      if (!fmt || !HAS_TOKEN.test(fmt)) return "";

      try {
        return formatPhp(this.sample, fmt);
      } catch (e) {
        return "";
      }
    },

    hydrate(raw) {
      this.parseError = false;

      if (!raw || !String(raw).trim()) {
        this.model = {DefaultFormat: "", FileFormat: "", Overrides: []};
        return;
      }

      try {
        const parsed = JSON.parse(raw);

        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("format inattendu");
        }

        this.model = {
          DefaultFormat: parsed.DefaultFormat || "",
          FileFormat: parsed.FileFormat || "",
          Overrides: Array.isArray(parsed.Overrides)
              ? parsed.Overrides.map((o) => ({
                from: o.from || "",
                to: o.to || "",
                Format: o.Format || "",
              }))
              : [],
        };
      } catch (e) {
        this.parseError = true;
      }
    },

    applyPreset(field, fmt) {
      this.$set(this.model, field, fmt);
      this.emit();
    },

    addOverride() {
      this.model.Overrides.push({from: "", to: "", Format: ""});
    },

    removeOverride(i) {
      this.model.Overrides.splice(i, 1);
      this.emit();
    },

    emit() {
      const out = {};

      if (this.model.DefaultFormat) {
        out.DefaultFormat = this.model.DefaultFormat;
      }

      if (this.model.FileFormat) {
        out.FileFormat = this.model.FileFormat;
      }

      // Une exception incomplète est ignorée : le back la rejetterait de toute façon.
      const overrides = this.model.Overrides.filter(
          (o) => o.from && o.to && o.Format
      );

      if (overrides.length) {
        out.Overrides = overrides;
      }

      const json = Object.keys(out).length ? JSON.stringify(out) : null;

      this.lastEmitted = json;
      this.$emit("input", json);
    },

    onRawInput(v) {
      this.lastEmitted = v;
      this.$emit("input", v || null);

      try {
        if (v && String(v).trim()) {
          JSON.parse(v);
        }
        this.parseError = false;
      } catch (e) {
        this.parseError = true;
      }
    },
  },
};
</script>

<style scoped>
.df-editor {
  width: 100%;
}

.df-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.df-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2d24;
}

.df-title .material-symbols-outlined {
  font-size: 18px;
}

.df-hint {
  font-size: 0.78rem;
  color: #8a9a90;
  margin: 5px 0 0;
  max-width: 640px;
}

.df-raw-toggle {
  background: none;
  border: 1px solid #d8e0db;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.72rem;
  color: #6b7a70;
  cursor: pointer;
  flex-shrink: 0;
}

.df-raw-toggle:hover {
  border-color: #2d5a3d;
  color: #2d5a3d;
}

.df-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: #a94442;
  background: #fdf3f2;
  border: 1px solid #f0d6d4;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.df-error .material-symbols-outlined {
  font-size: 18px;
}

.df-textarea {
  width: 100%;
  border: 1px solid #d8e0db;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 0.78rem;
  color: #1f2d24;
  background: #fff;
  resize: vertical;
}

.df-textarea:focus {
  outline: none;
  border-color: #2d5a3d;
}

.df-field {
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f3f1;
}

.df-field:last-of-type {
  border-bottom: none;
}

.df-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2d24;
  margin-bottom: 7px;
}

.df-label em {
  display: block;
  font-style: normal;
  font-weight: 400;
  font-size: 0.74rem;
  color: #8a9a90;
  margin-top: 2px;
}

.df-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.df-input {
  border: 1px solid #d8e0db;
  border-radius: 6px;
  padding: 6px 10px;
  font-family: monospace;
  font-size: 0.82rem;
  color: #1f2d24;
  background: #fff;
  min-width: 180px;
  flex: 1;
}

.df-input:focus {
  outline: none;
  border-color: #2d5a3d;
}

.df-input:disabled {
  background: #f6f8f7;
  color: #8a9a90;
}

.df-preview {
  font-size: 0.8rem;
  color: #2d5a3d;
  background: #eef5f0;
  border-radius: 6px;
  padding: 5px 10px;
  white-space: nowrap;
}

.df-preview.inline {
  display: block;
  margin-top: 4px;
  font-size: 0.72rem;
}

.df-preview.invalid {
  color: #a94442;
  background: #fdf3f2;
}

.df-sub {
  font-size: 0.74rem;
  color: #8a9a90;
  margin: 8px 0 0;
}

.df-sub code,
.df-cheat code {
  background: #f0f3f1;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.72rem;
  color: #2d5a3d;
}

.df-presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 9px;
}

.df-preset {
  background: #fff;
  border: 1px solid #e0e6e2;
  border-radius: 20px;
  padding: 3px 11px;
  font-size: 0.72rem;
  color: #6b7a70;
  cursor: pointer;
}

.df-preset:hover {
  border-color: #2d5a3d;
  color: #2d5a3d;
}

.df-empty {
  font-size: 0.8rem;
  color: #8a9a90;
  font-style: italic;
  padding: 8px 0;
}

.df-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-bottom: 10px;
}

.df-table th {
  text-align: left;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding: 4px 6px;
  border-bottom: 1px solid #e6ebe8;
}

.df-table td {
  padding: 5px 6px;
  vertical-align: top;
}

.df-action-col {
  width: 32px;
}

.df-remove {
  background: none;
  border: none;
  color: #b0bdb5;
  cursor: pointer;
  display: inline-flex;
  padding: 4px 2px;
}

.df-remove:hover {
  color: #d9534f;
}

.df-remove .material-symbols-outlined {
  font-size: 18px;
}

.df-add {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px dashed #c8d6cd;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.8rem;
  color: #2d5a3d;
  cursor: pointer;
}

.df-add:hover {
  border-color: #2d5a3d;
  background: #f6faf7;
}

.df-add .material-symbols-outlined {
  font-size: 16px;
}

.df-cheat {
  font-size: 0.78rem;
  color: #6b7a70;
  margin-top: 4px;
}

.df-cheat summary {
  cursor: pointer;
  color: #2d5a3d;
  font-size: 0.76rem;
}

.df-cheat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 5px 14px;
  margin-top: 9px;
  font-size: 0.74rem;
}
</style>
